import { AsyncButton, MosaicTrafficLightsValue } from "aic";
import { CommentScope } from "components/common/comments/CommentScope";
import { UPDATE_TLF } from "contexts/tlf/interface";
import { TlfContext } from "contexts/TlfContext";
import { ReactNode, useCallback, useContext, useState } from "react";
import { Image } from "react-bootstrap";
import { XCircle } from "react-bootstrap-icons";
import { useDropzone } from "react-dropzone";

export interface v1FigureBody {
  figureString: string;
  coreTrafficLight: MosaicTrafficLightsValue;
}

/**
 * V1 Figure component
 */
export const V1Figure = (): ReactNode => {
  const tlfContext = useContext(TlfContext);
  const tlfData = tlfContext?.tlfData;
  const figureData =
    tlfData && tlfData.body
      ? (tlfData.body as v1FigureBody)
      : { figureString: "", coreTrafficLight: "BlankNotEvaluated" };
  const [message, setMessage] = useState<string[]>([]);

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      /* istanbul ignore else */
      if (tlfContext && !tlfContext.readOnly) {
        // Set up check message
        const newMessage = [];

        // Check one image file has been loaded
        if (acceptedFiles.length !== 1) {
          newMessage.push("Only one file can be added.");
        }
        const newImage: File = acceptedFiles[0];
        if (["image/png", "image/jpeg"].indexOf(newImage.type) === -1) {
          newMessage.push("Only PNG and JPG images are accepted.");
        }
        if (newImage.size > 160000) {
          newMessage.push("Image must be 150K or less in size.");
        }

        // Update message here
        setMessage(newMessage);

        // Process if everything is ok
        if (newMessage.length === 0) {
          // Set up to convert to base64
          const fileReader = new FileReader();
          fileReader.onload = (fileLoadedEvent) => {
            /* istanbul ignore else */
            if (fileLoadedEvent.target) {
              const base64 = fileLoadedEvent.target.result;
              /* istanbul ignore else */
              if (typeof base64 === "string") {
                tlfContext.dispatch({
                  operation: UPDATE_TLF,
                  tlfData: {
                    body: {
                      figureString: base64,
                      coreTrafficLight: "BlankNotEvaluated",
                    },
                  },
                  calledFrom: "V1Figure:setFigureData",
                });
              }
            }
          };
          // Read image
          fileReader.readAsDataURL(newImage);
        }
      }
    },
    [tlfContext],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

  return !tlfData ? null : (
    <CommentScope
      id={`tlf-legacy-figure-${tlfData.id}`}
      activeContext="TlfItem"
      commentLabel="New figure comment"
      iconPosition={{ top: "0", left: "24px" }}
    >
      <div
        aria-label="Drop JPG or PNG image"
        {...getRootProps({
          style: {
            border: "1px blue dashed",
            borderRadius: "10px",
            padding: "1px",
            margin: "1rem",
            height: "603px",
          },
        })}
      >
        {!tlfContext.readOnly && (
          <input
            {...getInputProps()}
            id="dropzoneinput"
            aria-label="Dropzone input for image upload"
          />
        )}
        {figureData.figureString && figureData.figureString.startsWith("data:image") ? (
          <div
            style={{
              position: "relative",
            }}
            className="d-flex justify-content-center"
          >
            <Image
              src={figureData.figureString}
              style={{
                objectFit: "contain",
                objectPosition: "0%",
                height: "600px",
                maxWidth: "100%",
              }}
            />
            {!tlfContext.readOnly && (
              <div
                className="position-absolute"
                style={{ top: "0", right: "0" }}
              >
                <AsyncButton
                  id="tlf-figure-remove-button"
                  aria-label="Remove image"
                  variant="danger"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    tlfContext.dispatch({
                      operation: UPDATE_TLF,
                      tlfData: {
                        body: { figureString: "", coreTrafficLight: "BlankNotEvaluated" },
                      },
                      calledFrom: "V1Figure:newFigure",
                    });
                  }}
                >
                  <XCircle /> Remove
                </AsyncButton>
              </div>
            )}
          </div>
        ) : (
          <div
            style={{ height: "600px", position: "relative" }}
            className="d-flex justify-content-center align-items-center"
          >
            {isDragActive ? (
              <div>Drop the file here ...</div>
            ) : (
              <div>
                <div>Drag and drop a file here, or click to select file</div>
                {message.map((m, mi) => (
                  <div key={mi}>{m}</div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </CommentScope>
  );
};

V1Figure.displayName = "V1Figure";
